import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Image,
  SafeAreaView,
  ActivityIndicator,
  Modal,
  Alert,
  Platform
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../screens/ThemeContext';
import { collection, query, where, getDocs, orderBy, deleteDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import { doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../private/AuthContext';
import CentreLayout from './web-ui/CentreLayout';

const filtresVilles = ['Toutes les villes', 'Yaoundé', 'Douala', 'Bafoussam'];
const filtresConcours = ['Tous les concours', 'ENS', 'ENAM', 'POLICE', 'IRIC'];

// Ajoute la fonction showAlert compatible web/mobile
const showAlert = (title, message, buttons = null) => {
  if (Platform.OS === 'web') {
    if (buttons && buttons.length > 0) {
      const confirmed = window.confirm(`${title}\n\n${message}`);
      if (confirmed && buttons[1] && buttons[1].onPress) {
        buttons[1].onPress();
      }
    } else {
      window.alert(`${title}\n\n${message}`);
    }
  } else {
    if (buttons) {
      Alert.alert(title, message, buttons);
    } else {
      Alert.alert(title, message);
    }
  }
};

export default function CentresScreen({navigation}) {
  const { theme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [villeSelectionnee, setVilleSelectionnee] = useState('Toutes les villes');
  const [concoursSelectionne, setConcoursSelectionne] = useState('Tous les concours');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchUserAnnouncements();
    } else {
      setLoading(false);
      setAnnouncements([]);
    }
  }, [user]);

  const fetchUserAnnouncements = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const q = query(
        collection(db, 'announcements'),
        where('centerInfo.centerId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const announcementsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAnnouncements(announcementsData);
    } catch (error) {
      console.error('Error fetching user announcements:', error);
      Alert.alert('Erreur', 'Impossible de charger vos annonces');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const renderStatusBadge = (etat) => {
    const statusStyles = {
      approved: {
        container: styles.statusBadgeApproved,
        text: 'Approuvé',
        icon: 'check-circle'
      },
      rejected: {
        container: styles.statusBadgeRejected,
        text: 'Refusé',
        icon: 'close-circle'
      },
      pending: {
        container: styles.statusBadgePending,
        text: 'En attente',
        icon: 'clock-outline'
      }
    };
  
    // Si l'état est undefined, null ou une valeur non connue, considérer comme "pending"
    const status = statusStyles[etat] || statusStyles.pending;
  
    return (
      <View style={[styles.statusBadge, status.container]}>
        <MaterialCommunityIcons name={status.icon} size={16} color="white" style={styles.statusIcon} />
        <Text style={styles.statusText}>{status.text}</Text>
      </View>
    );
  };

  const renderAnnouncement = (announcement) => (
    <TouchableOpacity
      key={announcement.id}
      style={[styles.announcementCard, { backgroundColor: theme.surface, borderColor: theme.border }]}
      onPress={() => navigation.navigate('centreDetail', { 
        announcement: announcement
      })}
    >
      <Image
        source={{ uri: announcement.imageUrl }}
        style={styles.announcementImage}
      />
      <View style={styles.announcementContent}>
        <View style={styles.headerRow}>
          <View style={styles.centerInfo}>
            <Text style={[styles.centerName, { color: theme.text }]}>
              {announcement.centerInfo.centerName}
            </Text>
            <Text style={[styles.centerLocation, { color: theme.textSecondary }]}>
              {announcement.centerInfo.centerAddress}
            </Text>
          </View>
          <TouchableOpacity 
            style={styles.optionsButton}
            onPress={() => {
              setSelectedAnnouncement(announcement);
              setShowOptions(true);
            }}
          >
            <MaterialCommunityIcons 
              name="dots-vertical" 
              size={24} 
              color={theme.textSecondary} 
            />
          </TouchableOpacity>
        </View>
      
        <Text style={[styles.announcementTitle, { color: theme.text }]}>
          {announcement.titre}
        </Text>
        
        <Text style={[styles.announcementDescription, { color: theme.textSecondary }]} numberOfLines={2}>
          {announcement.description}
        </Text>

        <View style={styles.concoursContainer}>
          {announcement.concours.map((concours, index) => (
            <View 
              key={index}
              style={[styles.concoursBadge, { backgroundColor: theme.background }]}
            >
              <Text style={[styles.concoursText, { color: theme.primary }]}>{concours}</Text>
            </View>
          ))}
        </View>

        <View style={styles.dateInfo}>
          {renderStatusBadge(announcement.etat)}
        </View>
      </View>
    </TouchableOpacity>
  );

  const styles = StyleSheet.create({
    safeArea: {
      flex: 1,
    },
    container: {
      flex: 1,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
    },
    backButton: {
      padding: 8,
      borderRadius: 12,
      marginRight: 15,
    },
    headerTitle: {
      fontSize: 20,
      fontWeight: 'bold',
    },
    searchSection: {
      padding: 20,
      backgroundColor: theme.surface,
      margin: 20,
      borderRadius: 20,
      shadowColor: theme.text,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.05,
      shadowRadius: 15,
      elevation: 5,
    },
    searchBar: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.background,
      borderRadius: 12,
      paddingHorizontal: 15,
    },
    searchInput: {
      flex: 1,
      paddingVertical: 12,
      marginLeft: 10,
      fontSize: 16,
    },
    filtersContainer: {
      marginTop: 15,
    },
    filterChip: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 20,
      marginRight: 10,
      borderWidth: 1,
    },
    filterChipSelected: {
      backgroundColor: theme.primary,
      borderColor: theme.primary,
    },
    filterText: {
      color: theme.textSecondary,
    },
    filterTextSelected: {
      color: '#fff',
    },
    announcementsContainer: {
      padding: 20,
    },
    announcementCard: {
      borderRadius: 16,
      marginBottom: 20,
      borderWidth: 1,
      overflow: 'hidden',
    },
    announcementImage: {
      width: '100%',
      height: 200,
      backgroundColor: '#f0f0f0',
    },
    announcementContent: {
      padding: 15,
    },
    centerInfo: {
      marginBottom: 12,
    },
    centerName: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 4,
    },
    centerLocation: {
      fontSize: 14,
    },
    announcementTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 8,
    },
    announcementDescription: {
      fontSize: 14,
      lineHeight: 20,
      marginBottom: 12,
    },
    concoursContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      marginBottom: 12,
    },
    concoursBadge: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 20,
    },
    concoursText: {
      fontSize: 12,
      fontWeight: '500',
    },
    dateInfo: {
      justifyContent: 'space-between',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    dateText: {
      fontSize: 12,
    },
    loader: {
      marginTop: 40,
    },
    emptyText: {
      textAlign: 'center',
      marginTop: 40,
      fontSize: 16,
    },
    headerRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 12,
    },
    optionsButton: {
      padding: 4,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    modalContent: {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      padding: 20,
    },
    modalTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 20,
      textAlign: 'center',
    },
    optionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 15,
      borderRadius: 12,
      marginBottom: 12,
    },
    optionButtonText: {
      color: '#fff',
      fontSize: 16,
      fontWeight: '600',
      marginLeft: 10,
    },
    statusBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 20,
    },
    statusBadgeApproved: {
      backgroundColor: '#4CAF50',
    },
    statusBadgeRejected: {
      backgroundColor: '#FF5252',
    },
    statusIcon: {
      marginRight: 4,
    },
    statusText: {
      color: 'white',
      fontSize: 12,
      fontWeight: '600',
    },
    statusBadgePending: {
      backgroundColor: '#FFC107',
    },
    noAccountContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    loginButton: {
      marginTop: 20,
      backgroundColor: theme.primary,
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: 12,
    },
    loginButtonText: {
      color: '#fff',
      fontSize: 16,
      fontWeight: '600',
    },
    addButton: {
      position: 'absolute',
      bottom: 20,
      right: 20,
      width: 60,
      height: 60,
      borderRadius: 30,
      backgroundColor: theme.primary,
      justifyContent: 'center',
      alignItems: 'center',
      elevation: 4,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
    },
    bottomSpace: {
      height: 80,
    },
  });

  const isWeb = typeof window !== 'undefined' && !!window.document;

  const headerBlock = (
    <View style={[styles.header, { backgroundColor: theme.background }]}>
      <TouchableOpacity
        onPress={() => navigation.goBack()}
        style={[styles.backButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
      >
        <MaterialCommunityIcons name="arrow-left" size={24} color={theme.primary} />
      </TouchableOpacity>
      <Text style={[styles.headerTitle, { color: theme.text }]}>Mes Annonces</Text>
    </View>
  );

  if (isWeb) {
    return (
      <CentreLayout title="Mes annonces" subtitle="Toutes les annonces que votre centre a publiées">
        {!user ? (
          <View style={styles.noAccountContainer}>
            <MaterialCommunityIcons name="account-off" size={60} color="#ccc" />
            <Text style={[styles.emptyText, { color: theme.textSecondary }]}>
              Vous devez être connecté pour voir vos annonces
            </Text>
          </View>
        ) : (
          <View style={styles.announcementsContainer}>
            {loading ? (
              <View style={{ padding: 40, alignItems: 'center' }}>
                <Text style={{ color: theme.textSecondary }}>Chargement...</Text>
              </View>
            ) : announcements.length === 0 ? (
              <View style={styles.noAccountContainer}>
                <MaterialCommunityIcons name="bullhorn-outline" size={60} color="#ccc" />
                <Text style={[styles.emptyText, { color: theme.textSecondary }]}>
                  Aucune annonce pour le moment
                </Text>
              </View>
            ) : (
              announcements.map((item) => renderAnnouncement(item))
            )}
          </View>
        )}
      </CentreLayout>
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.primary }]}>
      {headerBlock}

      {!user ? (
        <View style={[styles.container, { backgroundColor: theme.background }]}>
          <View style={styles.noAccountContainer}>
            <MaterialCommunityIcons name="account-off" size={60} color="#ccc" />
            <Text style={[styles.emptyText, { color: theme.textSecondary }]}>
              Vous devez être connecté pour voir vos annonces
            </Text>
            <TouchableOpacity
              style={styles.loginButton}
              onPress={() => navigation.navigate('LoginScreen')}
            >
              <Text style={styles.loginButtonText}>Se connecter</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        <>
          <ScrollView style={[styles.container, { backgroundColor: theme.background }]}>
            <View style={styles.announcementsContainer}>
              {loading ? (
                <ActivityIndicator size="large" color={theme.primary} style={styles.loader} />
              ) : announcements.length > 0 ? (
                announcements.map(announcement => renderAnnouncement(announcement))
              ) : (
                <Text style={[styles.emptyText, { color: theme.textSecondary }]}>
                  Vous n'avez pas encore d'annonces
                </Text>
              )}
            </View>
            <View style={styles.bottomSpace} />
          </ScrollView>

          <TouchableOpacity
            style={styles.addButton}
            onPress={() => navigation.navigate('CreateContentScreen')}
          >
            <MaterialCommunityIcons name="plus" size={30} color="#fff" />
          </TouchableOpacity>
        </>
      )}

      <View style={[styles.container, { backgroundColor: theme.background, flex: 1 }]}/>

      <Modal
        visible={showOptions}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowOptions(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.surface }]}>
            <Text style={[styles.modalTitle, { color: theme.text }]}>
              Options de l'annonce
            </Text>
            
            <TouchableOpacity 
              style={[styles.optionButton, { backgroundColor: theme.primary }]}
              onPress={() => {
                setShowOptions(false);
                navigation.navigate('EditContentScreen', { 
                  announcement: selectedAnnouncement 
                });
              }}
            >
              <MaterialCommunityIcons name="pencil" size={20} color="#fff" />
              <Text style={styles.optionButtonText}>Modifier l'annonce</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.optionButton, { backgroundColor: '#FF9800' }]}
              onPress={() => {
                setShowOptions(false);
                navigation.navigate('RenewAnnouncementScreen', { 
                  announcement: selectedAnnouncement 
                });
              }}
            >
              <MaterialCommunityIcons name="refresh" size={20} color="#fff" />
              <Text style={styles.optionButtonText}>Renouveler l'annonce</Text>
            </TouchableOpacity>
      
            <TouchableOpacity 
              style={[styles.optionButton, { backgroundColor: '#dc3545' }]}
              onPress={() => {
                showAlert(
                  'Confirmation',
                  'Voulez-vous vraiment supprimer cette annonce ?',
                  [
                    {
                      text: 'Annuler',
                      style: 'cancel'
                    },
                    {
                      text: 'Supprimer',
                      style: 'destructive',
                      onPress: async () => {
                        try {
                          await deleteDoc(doc(db, 'announcements', selectedAnnouncement.id));
                          setShowOptions(false);
                          fetchUserAnnouncements(); // Rafraîchir la liste
                          Alert.alert('Succès', 'L\'annonce a été supprimée');
                        } catch (error) {
                          console.error('Error deleting announcement:', error);
                          Alert.alert('Erreur', 'Impossible de supprimer l\'annonce');
                        }
                      }
                    }
                  ]
                );
              }}
            >
              <MaterialCommunityIcons name="delete" size={20} color="#fff" />
              <Text style={styles.optionButtonText}>Supprimer l'annonce</Text>
            </TouchableOpacity>
      
            <TouchableOpacity 
              style={[styles.optionButton, { backgroundColor: theme.textSecondary }]}
              onPress={() => setShowOptions(false)}
            >
              <MaterialCommunityIcons name="close" size={20} color="#fff" />
              <Text style={styles.optionButtonText}>Fermer</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}